/**
 * "Add trajectory from LangSmith" (TRA-1087, path 2).
 *
 * The action of the Trajectories page's empty state when the search box
 * holds a LangSmith run id the platform never recorded (TRA-1169) — runs
 * from before the client sent the run-id header have no gateway trace to
 * find. The user exports the trace from LangSmith, picks the file here,
 * and the gateway stores it as a normal trajectory (one turn per model
 * call). On success the run opens the same way a row click does.
 *
 * The dialog also asks what is wrong with the run. An upload must never
 * sit only in the Trajectories list (nothing scans imported runs, so no
 * flag would ever come on its own): with a verdict the api saves it as a
 * reviewed comment on the run's first turn and the run feeds evals; with
 * the review left empty the api flags the run for the review queue. More
 * comments, per turn, can follow on the conversation page the run opens on.
 *
 * The api rejects a file for a different run than the one searched for,
 * so a wrong download surfaces as a plain sentence, not a mislabeled run.
 *
 * Before the upload the export is trimmed to what the gateway stores (the
 * model calls; wrapper steps lose their repeated message bodies), so a
 * batch run's 60 MB download goes up as the ~12 MB the gateway would have
 * seen had the run gone through it live. The api's 25 MB cap then bounds
 * the model calls alone.
 */

import { useQueryClient } from '@tanstack/react-query'
import { Upload } from 'lucide-react'
import { useId, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { toast } from 'sonner'

import { customFetch } from '@/api/client'
import { verdictPillClass } from '@/components/review/verdictPill'
import { useConversationLinkState } from '@/components/traces/conversationLink'
import { getListTrajectoriesApiTrajectoriesGetQueryKey } from '@/api/generated/trajectories/trajectories'
import type { TrajectoryImportRequest, TrajectoryImportResponse } from '@/api/generated/model'
import {
  exportByteLength,
  importedRunMessage,
  importedRunUrl,
  readExportFile,
  slimExport,
  slimmedUploadProblem,
  uploadProblem,
  uploadReview,
  type UploadVerdict,
} from '@/components/trajectories/langsmithImport'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'

interface LangsmithImportDialogProps {
  /** The run id in the search box — sent along so the wrong file is refused. */
  runId: string
}

/**
 * How to get the file out of LangSmith. Written for someone who has the
 * run open in a support ticket, not for us. Keep it to what they click.
 */
const EXPORT_STEPS = [
  'In LangSmith open Tracing, then the project your agent traces into.',
  'Open the run you want to add.',
  'Click "More actions", then "Download", then "Download trace" (not "Download run"). A .json file downloads.',
  'Pick that file below.',
]

export function LangsmithImportDialog({ runId }: LangsmithImportDialogProps): React.JSX.Element {
  const [open, setOpen] = useState(false)
  const [file, setFile] = useState<File | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [pending, setPending] = useState(false)
  const [verdict, setVerdict] = useState<UploadVerdict | null>(null)
  const [comment, setComment] = useState('')
  const fileId = useId()
  const commentId = useId()
  const navigate = useNavigate()
  // The imported run's page is reached from Trajectories; its back arrow
  // returns here.
  const linkState = useConversationLinkState('trajectories')
  const queryClient = useQueryClient()

  function reset(): void {
    setFile(null)
    setError(null)
    setPending(false)
    setVerdict(null)
    setComment('')
  }

  async function submit(event: React.FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault()
    const problem = uploadProblem(file)
    if (problem !== null || file === null) {
      setError(problem ?? 'Pick the exported JSON file first.')
      return
    }
    const review = uploadReview(verdict, comment)
    if (review.kind === 'error') {
      setError(review.message)
      return
    }
    setError(null)
    setPending(true)
    // Everything that can fail sits inside one try, and `pending` is
    // released in its finally: a rejected read or request must never
    // leave the dialog's buttons disabled.
    try {
      // The browser reads and parses the file: a non-JSON download is
      // caught here, and the api takes plain JSON instead of multipart.
      const parsed = await readExportFile(file)
      if (parsed.kind === 'error') {
        setError(parsed.message)
        return
      }
      // A batch run's export is mostly wrapper steps repeating the same
      // messages; the gateway stores only the model calls. Trim here so
      // the upload carries what the gateway would have seen live, and
      // measure what is left against the api's cap.
      const slim = slimExport(parsed.export)
      const tooBig = slimmedUploadProblem(exportByteLength(slim.export))
      if (tooBig !== null) {
        setError(tooBig)
        return
      }
      const body: TrajectoryImportRequest = {
        export: slim.export,
        langsmith_run_id: runId,
        review: review.review,
      }
      const result = await customFetch<TrajectoryImportResponse>('/api/trajectories/import', {
        method: 'POST',
        data: body,
      })
      // The listing may now contain the run (when the range covers its
      // real date); refresh it either way so a back-navigation is current.
      void queryClient.invalidateQueries({
        queryKey: getListTrajectoriesApiTrajectoriesGetQueryKey(),
      })
      toast.success(importedRunMessage(result))
      const url = importedRunUrl(result)
      setOpen(false)
      reset()
      if (url !== null) navigate(url, { state: linkState })
    } catch (caught) {
      // ApiRequestError carries the api's `detail` — the gateway's own
      // sentence for a bad file, or a 502 for a gateway fault.
      setError(caught instanceof Error ? caught.message : String(caught))
    } finally {
      setPending(false)
    }
  }

  return (
    <>
      <Button
        type="button"
        onClick={() => {
          reset()
          setOpen(true)
        }}
      >
        <Upload className="mr-2 h-4 w-4" />
        Add this trajectory from LangSmith
      </Button>
      <Dialog
        open={open}
        onOpenChange={(next) => {
          if (pending) return
          setOpen(next)
          if (!next) reset()
        }}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Add trajectory from LangSmith</DialogTitle>
            <DialogDescription>
              Runs recorded before the run id was sent to TrainLoop can only be added from
              their LangSmith export. This adds run{' '}
              <code className="font-mono text-xs">{runId}</code>.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={(event) => void submit(event)} className="space-y-4">
            <ol className="list-decimal space-y-1 pl-5 text-sm text-muted-foreground">
              {EXPORT_STEPS.map((step) => (
                <li key={step}>{step}</li>
              ))}
            </ol>
            <div className="space-y-2">
              <Label htmlFor={fileId}>LangSmith export (.json)</Label>
              <Input
                id={fileId}
                type="file"
                accept=".json,application/json"
                disabled={pending}
                onChange={(event) => {
                  setFile(event.target.files?.[0] ?? null)
                  setError(null)
                }}
              />
            </div>
            <fieldset className="space-y-2" disabled={pending}>
              <legend className="text-sm font-medium">Is something wrong with this run?</legend>
              <p className="text-sm text-muted-foreground">
                Your answer is saved as the review of this run and feeds evals. Leave it empty
                and the run is flagged for the review queue instead.
              </p>
              <div className="flex items-center gap-1.5">
                <VerdictPill
                  tone="bad"
                  active={verdict === 'bad'}
                  onClick={() => {
                    setVerdict(verdict === 'bad' ? null : 'bad')
                    setError(null)
                  }}
                />
                <VerdictPill
                  tone="good"
                  active={verdict === 'good'}
                  onClick={() => {
                    setVerdict(verdict === 'good' ? null : 'good')
                    setError(null)
                  }}
                />
              </div>
              <Label htmlFor={commentId} className="sr-only">
                What went wrong?
              </Label>
              <Textarea
                id={commentId}
                rows={2}
                className="min-h-[2lh]"
                placeholder="What went wrong?"
                value={comment}
                onChange={(event) => {
                  setComment(event.target.value)
                  setError(null)
                }}
              />
            </fieldset>
            {error !== null && (
              <p role="alert" className="text-sm text-destructive">
                {error}
              </p>
            )}
            <DialogFooter className="flex justify-end gap-2">
              <Button
                type="button"
                variant="outline"
                disabled={pending}
                onClick={() => {
                  setOpen(false)
                  reset()
                }}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={pending || file === null}>
                <Upload className="mr-2 h-4 w-4" />
                {pending ? 'Adding…' : 'Add trajectory'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </>
  )
}

interface VerdictPillProps {
  tone: UploadVerdict
  active: boolean
  onClick: () => void
}

/** The review composer's Bad/Good pill, one per verdict; a click on the active pill clears it. */
function VerdictPill({ tone, active, onClick }: VerdictPillProps): React.JSX.Element {
  return (
    <button
      type="button"
      aria-pressed={active}
      onClick={onClick}
      className={`${verdictPillClass(active, tone)} disabled:opacity-50`}
    >
      {tone === 'bad' ? 'Bad' : 'Good'}
    </button>
  )
}
